/**
 * "Indicator values" — the rail's fifth button (§D3 manual submissions, M6 §5).
 *
 * D.2 and E.1 have no pipeline behind them: somebody counts the institutions, somebody
 * counts the hectares, and until this panel the only way that number reached the portal was
 * a database write. Now it arrives the way every other datum does — as an upload, with a
 * submitter's name on it, through the review queue — and the panel's whole job is to make
 * the file that carries it.
 *
 * **Two routes, one submission.** The grid is for a reporter with the portal open and a
 * dozen units to fill; the CSV tab is for a round already being run in a spreadsheet. Both
 * end in the same call: a `.csv` whose header is the template's, `kind_hint:
 * "indicator_values"`, and the four pieces of metadata (`indicator_id`, `period`, `level`,
 * `country`) the validator needs to know what the file is *about*. A CSV without them is
 * just a table of numbers.
 *
 * **Only indicators a person can report are offered.** Asking for D.2 by hand is reporting;
 * asking for an indicator MELS publishes would be overwriting an upstream system from a
 * text box, and the reviewer approving it would have no way to know they had.
 *
 * Two kinds qualify, and the filter names both (final review, 2026-09-21). `source ===
 * 'manual'` is the catalogue's own declaration of where an indicator is meant to come from
 * — D.2 and E.1. `aggregation === 'none'` is the survey series: a baseline estimate has no
 * roll-up, it is measured by a campaign rather than computed, and spec §3 has the survey
 * **re-issued** into a later round — which somebody has to be able to submit. The server
 * agrees already: `submissions.validate`'s `indicator_known` check accepts an indicator
 * that is `source="manual"` *or* `NO_AGGREGATION`, so the narrower client filter was
 * offering less than the endpoint would take.
 *
 * **The grid is prefilled from `/current`, and blank means blank.** A reporter reporting a
 * change sees what is on file and edits one row; a reporter reporting a round sees eighty
 * empty boxes and fills the ones that reported. An untouched empty box is submitted as an
 * empty field, counted in the submission's `diff.blank`, and changes nothing on approval.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Btn } from '../../../components/Btn';
import {
  fetchIndicatorCurrent,
  fetchIndicators,
  queryKeys,
  type Indicator,
  type IndicatorCurrentUnit,
} from '../../../lib/api';
import { NO_VALUE, fmtNum } from '../../../lib/format';
import { COUNTRY_NAMES, type Country } from '../../../lib/state';
import { toast } from '../../../lib/toast';
import { startUpload } from '../../../lib/upload';
import { applyEdit, coerceCell, initGrid, type GridState } from '../grid';
import { isEmail } from '../manager';
import { SubmitterFields } from '../SubmitterFields';
import { readSubmitter, writeSubmitter } from '../submitter';
import { ValuesCsv } from './ValuesCsv';
import { ValuesGrid } from './ValuesGrid';
import {
  LEVELS,
  PERIODS,
  PROGRAMME_ROW_ID,
  defaultPeriod,
  dirtyRows,
  parseLevel,
  submissionFilename,
  rowIdOf,
  submissionMetadata,
  toCsv,
  type Level,
  type ValueRow,
} from './values';

/** The two editable columns, in the order the grid draws them. */
const COLUMNS = [
  { key: 'value', label: 'value', kind: 'number' as const, editable: true },
  { key: 'note', label: 'note', kind: 'text' as const, editable: true },
];

const COUNTRIES: Country[] = ['ALL', 'BFA', 'MLI', 'NER', 'NGA'];

const EMPTY_STATE: GridState = { rows: [], original: {}, log: [], seq: 0 };

/** The two routes to the same submission, in the order the tabs sit in. */
const TABS = ['grid', 'csv'] as const;

type ValuesTab = (typeof TABS)[number];

export interface ValuesPanelProps {
  /** Sends the reader to the Queue, where their submission is now waiting. */
  onSubmitted?: () => void;
}

export function ValuesPanel({ onSubmitted }: ValuesPanelProps) {
  const { t } = useTranslation();

  const [indicatorId, setIndicatorId] = useState('');
  const [period, setPeriod] = useState(() => defaultPeriod());
  const [level, setLevel] = useState<Level>('0');
  const [country, setCountry] = useState<Country>('ALL');
  const [tab, setTab] = useState<ValuesTab>('grid');

  const stored = useMemo(() => readSubmitter(), []);
  const [who, setWho] = useState(stored);

  const [state, setState] = useState<GridState>(EMPTY_STATE);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, 'value'>>({});
  const [busy, setBusy] = useState(false);

  const indicators = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });

  /**
   * Only what a person may report. `source` is the §C1 declaration, not the provenance of
   * whatever values happen to be on file — an indicator declared `mels` with no values yet
   * is still not one to type numbers into.
   */
  const manual = useMemo<Indicator[]>(
    () =>
      (indicators.data ?? []).filter(
        // The two kinds a person reports — see the header. Mirrors the server's own
        // `indicator_known` check rather than restating half of it.
        (row) => row.source === 'manual' || row.aggregation === 'none',
      ),
    [indicators.data],
  );

  // The first manual indicator, once the list arrives — the panel is useless with no
  // indicator chosen, and there is no meaningful "choose one" state to sit in.
  useEffect(() => {
    if (indicatorId === '' && manual.length > 0) setIndicatorId(manual[0]!.id);
  }, [indicatorId, manual]);

  const indicator = manual.find((row) => row.id === indicatorId) ?? null;

  /*
    The programme total has no `org_units` row to filter, so a country left over from a
    previous selection is not a narrowing there — it is a claim about the submission that
    the server would store and a reviewer would read. The select is disabled at that level
    and the value is ignored with it.
  */
  const scopeCountry: Country = level === 'programme' ? 'ALL' : country;

  const current = useQuery({
    queryKey: queryKeys.indicatorCurrent(indicatorId, period, level, scopeCountry),
    queryFn: () =>
      fetchIndicatorCurrent(indicatorId, {
        period,
        level,
        country: scopeCountry === 'ALL' ? null : scopeCountry,
      }),
    enabled: indicatorId !== '',
  });

  const units = useMemo<IndicatorCurrentUnit[]>(() => current.data?.units ?? [], [current.data]);

  /*
    A new answer is a new grid. Keyed on the request rather than on the payload so a refetch
    of the same four selects — a window refocus, say — cannot silently throw away an edit
    the reporter has not submitted yet.
  */
  const answerKey = `${current.data?.indicator_id ?? ''}|${current.data?.period ?? ''}|${
    current.data?.level ?? ''
  }|${scopeCountry}`;
  useEffect(() => {
    setState(
      initGrid(
        units.map((unit) => ({
          _id: rowIdOf(unit),
          value: unit.value,
          note: null,
        })),
        COLUMNS,
      ),
    );
    setDrafts({});
    setErrors({});
    // `units` is derived from the same answer `answerKey` names; listing it as well would
    // reset the grid on every render that produced a new array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerKey]);

  const onCell = useCallback((rowId: string, key: 'value' | 'note', raw: string) => {
    if (key === 'value') {
      setDrafts((d) => ({ ...d, [rowId]: raw }));
      const coerced = coerceCell(raw, 'number');
      setErrors((e) => {
        if (coerced.ok) {
          if (!(rowId in e)) return e;
          const next = { ...e };
          delete next[rowId];
          return next;
        }
        return { ...e, [rowId]: 'value' };
      });
      // A box that does not hold a number holds no *value* either: the cell is blanked
      // rather than left on the last thing that parsed, so a submission can never carry a
      // number the reporter has since typed over.
      setState((s) => applyEdit(s, rowId, 'value', coerced.ok ? coerced.value : null));
      return;
    }
    setState((s) => applyEdit(s, rowId, 'note', raw.trim() === '' ? null : raw));
  }, []);

  /*
    The same `tablist` pattern as the page's own action rail (`features/manager/index.tsx`):
    a roving tabindex so Tab enters the strip once and lands on the selected tab, ←/→ (and
    Home/End) to move between them, and one real `tabpanel` the tabs point at. Two tabs is
    not too few for the pattern — a reader who knows tabs expects the arrows to work, and a
    strip that ignores them reads as broken rather than as simplified.
  */
  const tabsRef = useRef<HTMLDivElement>(null);
  const onTabKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const i = TABS.indexOf(tab);
      let next = i;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % TABS.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + TABS.length) % TABS.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = TABS.length - 1;
      else return;
      e.preventDefault();
      setTab(TABS[next]!);
      tabsRef.current
        ?.querySelector<HTMLButtonElement>(`[data-testid="mgr-iv-tab-${TABS[next]}"]`)
        ?.focus();
    },
    [tab],
  );

  const dirty = dirtyRows(state);
  const submitterOk = who.name.trim() !== '' && isEmail(who.email);
  const badCells = Object.keys(errors).length;

  const rowsForCsv = useCallback(
    (): ValueRow[] =>
      units.map((unit) => {
        const id = rowIdOf(unit);
        const row = state.rows.find((r) => r.id === id);
        return {
          unit_id: id === PROGRAMME_ROW_ID ? '' : unit.id,
          name: unit.name,
          country: unit.country,
          value: (row?.values.value ?? null) as number | null,
          note: String(row?.values.note ?? ''),
        };
      }),
    [state, units],
  );

  const send = useCallback(
    async (file: File) => {
      if (!indicator) return;
      setBusy(true);
      try {
        await startUpload(file, {
          kindHint: 'indicator_values',
          submitterName: who.name.trim(),
          submitterEmail: who.email.trim(),
          metadata: submissionMetadata({
            indicatorId: indicator.id,
            indicatorName: indicator.name,
            period,
            level,
            country: scopeCountry,
          }),
        }).done;
        writeSubmitter({ name: who.name.trim(), email: who.email.trim() });
        toast(t('manager.values.sent', { indicator: indicator.name, period }));
        onSubmitted?.();
      } catch (err) {
        toast(t('manager.values.sendFailed', { message: String((err as Error)?.message ?? err) }));
      } finally {
        setBusy(false);
      }
    },
    [indicator, level, onSubmitted, period, scopeCountry, t, who],
  );

  const onSubmit = useCallback(() => {
    if (!indicator) return;
    const csv = toCsv(rowsForCsv(), period);
    const file = new File([csv], submissionFilename(indicator.id, period, level), {
      type: 'text/csv',
    });
    void send(file);
  }, [indicator, level, period, rowsForCsv, send]);

  const fmt = useCallback(
    (value: number | null) => (value === null ? NO_VALUE : fmtNum(value, indicator?.fmt ?? 1)),
    [indicator],
  );

  return (
    <section className="card mgr-panelcard" data-testid="mgr-values">
      <div className="card-head">
        <div className="card-head-text">
          <h3>{t('manager.values.title')}</h3>
          <p className="sub">{t('manager.values.sub')}</p>
        </div>
      </div>

      <div className="mgr-form mgr-iv-scope" data-testid="mgr-iv-scope">
        <label className="mgr-field" htmlFor="mgr-iv-indicator">
          <span className="mgr-label">{t('manager.values.indicator')}</span>
          <select
            id="mgr-iv-indicator"
            data-testid="mgr-iv-indicator"
            className="mgr-input"
            value={indicatorId}
            onChange={(e) => setIndicatorId(e.target.value)}
          >
            {manual.length === 0 ? <option value="">{NO_VALUE}</option> : null}
            {manual.map((row) => (
              <option key={row.id} value={row.id}>
                {row.code ? `${row.code} ${row.name}` : row.name}
              </option>
            ))}
          </select>
          <span className="mgr-hint">{t('manager.values.indicatorHint')}</span>
        </label>

        <label className="mgr-field" htmlFor="mgr-iv-period">
          <span className="mgr-label">{t('manager.values.period')}</span>
          <select
            id="mgr-iv-period"
            data-testid="mgr-iv-period"
            className="mgr-input"
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
          >
            {PERIODS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </label>

        <label className="mgr-field" htmlFor="mgr-iv-level">
          <span className="mgr-label">{t('manager.values.level')}</span>
          <select
            id="mgr-iv-level"
            data-testid="mgr-iv-level"
            className="mgr-input"
            value={level}
            onChange={(e) => setLevel(parseLevel(e.target.value) ?? level)}
          >
            {LEVELS.map((name) => (
              <option key={name} value={name}>
                {t(`manager.values.levels.${name}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="mgr-field" htmlFor="mgr-iv-country">
          <span className="mgr-label">{t('manager.values.country')}</span>
          <select
            id="mgr-iv-country"
            data-testid="mgr-iv-country"
            className="mgr-input"
            value={country}
            disabled={level === 'programme'}
            onChange={(e) => setCountry(e.target.value as Country)}
          >
            {COUNTRIES.map((code) => (
              <option key={code} value={code}>
                {COUNTRY_NAMES[code]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <SubmitterFields
        name={who.name}
        email={who.email}
        onChange={(patch) => setWho((w) => ({ ...w, ...patch }))}
        idPrefix="mgr-iv"
        blockId="mgr-iv-submitter"
      />

      <div
        className="mgr-iv-tabs"
        role="tablist"
        aria-label={t('manager.values.title')}
        ref={tabsRef}
        onKeyDown={onTabKeyDown}
      >
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            id={`mgrIvTab-${name}`}
            className={tab === name ? 'mgr-iv-tab on' : 'mgr-iv-tab'}
            aria-selected={tab === name}
            aria-controls="mgrIvPanel"
            tabIndex={tab === name ? 0 : -1}
            data-testid={`mgr-iv-tab-${name}`}
            onClick={() => setTab(name)}
          >
            {t(`manager.values.tab.${name}`)}
          </button>
        ))}
      </div>

      <div
        className="mgr-iv-panel"
        id="mgrIvPanel"
        role="tabpanel"
        tabIndex={0}
        aria-labelledby={`mgrIvTab-${tab}`}
      >
      {tab === 'grid' ? (
        <>
          {current.isLoading ? <p className="mgr-hint">{t('manager.values.loading')}</p> : null}
          {current.isError ? (
            <p className="mgr-error" role="alert" data-testid="mgr-iv-error">
              {t('manager.values.loadFailed')}
            </p>
          ) : null}
          {!current.isLoading && !current.isError && units.length === 0 ? (
            <p className="mgr-hint" data-testid="mgr-iv-empty">
              {t('manager.values.noUnits')}
            </p>
          ) : null}
          {units.length > 0 ? (
            <ValuesGrid
              units={units}
              state={state}
              drafts={drafts}
              errors={errors}
              onCell={onCell}
              fmt={fmt}
            />
          ) : null}

          {/*
            The footer is a bar of its own below the grid, not the grid's last row (client,
            2026-09-22: *"the submit for review button is very close to the grid"*). What the
            reader is owed before they press it — how many rows would change, and any cell
            that is not a number — is grouped on the left, and the button sits at the right
            edge with 16px of air on every side of both.
          */}
          <div className="mgr-grid-foot">
            <div className="mgr-grid-foot-info">
              <span className="mgr-grid-count" data-testid="mgr-iv-dirty">
                {t('manager.values.dirty', { n: dirty })}
              </span>
              {badCells > 0 ? (
                <span className="mgr-error" role="alert" data-testid="mgr-iv-bad">
                  {t('manager.values.bad', { n: badCells })}
                </span>
              ) : null}
            </div>
            <Btn
              variant="primary"
              data-testid="mgr-iv-submit"
              disabled={!submitterOk || !indicator || busy || dirty === 0 || badCells > 0}
              onClick={onSubmit}
            >
              {busy ? t('manager.values.sending') : t('manager.values.submit')}
            </Btn>
          </div>
          {submitterOk ? null : (
            <p className="mgr-hint" data-testid="mgr-iv-needs-submitter">
              {t('manager.submitter.needed')}
            </p>
          )}
        </>
      ) : (
        <ValuesCsv
          indicatorId={indicatorId}
          level={level}
          period={period}
          country={scopeCountry === 'ALL' ? null : scopeCountry}
          enabled={submitterOk && indicator !== null && !busy}
          onPick={(file) => void send(file)}
        />
      )}
      </div>
    </section>
  );
}

export default ValuesPanel;
